import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  FileText, Users, MessageSquare, Target, Briefcase, 
  Network, Search, Clock, DollarSign, Goal, UserPlus, 
  Heart, UsersRound, ChevronRight, Sparkles, ArrowRight, Zap
} from 'lucide-react';

// Generic business/HR focused images (not focusing on specific people)
const SERVICE_IMAGES = {
  professional: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80",
  career: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80",
  life: "https://images.unsplash.com/photo-1497215842964-222b430dc094?w=800&q=80",
  mentorship: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80",
};

const services = [
  {
    id: 'professional-readiness',
    title: 'Professional Readiness Training',
    tagline: 'Build the skills to enter or re-enter the workforce with confidence.',
    description: 'Hands-on training focused on workplace readiness, communication, and professionalism.',
    icon: Briefcase,
    color: 'purple',
    image: SERVICE_IMAGES.professional,
    features: [
      { icon: FileText, text: 'Resume and cover letter development' },
      { icon: MessageSquare, text: 'Interview preparation and mock interviews' },
      { icon: Users, text: 'Workplace etiquette and professional communication training' },
    ]
  },
  {
    id: 'career-development',
    title: 'Career Development & Guidance',
    tagline: 'Get clarity on your next career move.',
    description: 'Plain-language guidance to support career transitions, advancement, and long-term growth planning.',
    icon: Target,
    color: 'teal',
    image: SERVICE_IMAGES.career,
    features: [
      { icon: UserPlus, text: 'One-on-one career coaching' },
      { icon: Network, text: 'Networking with industry professionals' },
      { icon: Search, text: 'Job search strategies' },
    ]
  },
  {
    id: 'life-skills',
    title: 'Life Skills & Personal Growth',
    tagline: 'Strengthen the foundation that supports career success.',
    description: 'Resources designed to build confidence, resilience, and decision-making for everyday life and work.',
    icon: Goal,
    color: 'purple',
    image: SERVICE_IMAGES.life,
    features: [
      { icon: Clock, text: 'Time management and productivity coaching' },
      { icon: DollarSign, text: 'Financial literacy and budgeting workshops' },
      { icon: Target, text: 'Goal setting with accountability partnerships' },
    ]
  },
  {
    id: 'mentorship',
    title: 'Mentorship & Community Support',
    tagline: 'Grow with people who understand your journey.',
    description: 'A supportive community offering mentorship, shared learning, and encouragement.',
    icon: Heart,
    color: 'teal',
    image: SERVICE_IMAGES.mentorship,
    features: [
      { icon: UserPlus, text: 'One-on-one and group mentorship' },
      { icon: Heart, text: 'Encouragement through life transitions' },
      { icon: UsersRound, text: 'Small-group support circles' },
    ]
  },
];

export const Services = () => {
  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-hr-warm-bg via-white to-hr-teal-light/20 relative" data-testid="services-hero">
        <div className="absolute top-0 right-0 w-96 h-96 bg-hr-purple/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-hr-teal/10 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-1/2 left-1/3 w-40 h-40 bg-hr-purple/5 rounded-full blur-2xl animate-blob" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-purple/10 mb-6 animate-bounce-subtle">
              <Sparkles className="h-4 w-4 text-hr-purple animate-pulse" />
              <span className="text-hr-purple text-sm font-medium">What We Offer</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-hr-text-primary animate-fade-in-up">
              Our <span className="text-gradient bg-gradient-to-r from-hr-purple to-hr-teal bg-clip-text text-transparent animate-gradient">Services</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Everything you need to thrive in your career journey. From professional readiness to mentorship, we provide comprehensive support designed for real-world success.
            </p>
            
            {/* Pricing Info */}
            <div className="mt-8 inline-flex flex-col items-center gap-2 px-6 py-4 rounded-2xl bg-hr-teal/10 animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
              <p className="text-lg font-semibold text-hr-teal">
                Pricing Vary per Service Request.
              </p>
            </div>
            
            <div className="mt-10 animate-fade-in-up stagger-3" style={{ opacity: 0 }}>
              <Link to="/contact" data-testid="services-contact-btn">
                <Button className="group bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full px-10 py-7 text-lg shadow-2xl hover:shadow-hr-purple/40 transition-all duration-500 hover:-translate-y-3 hover:scale-105 btn-3d animate-glow">
                  Get Started Today
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white relative" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-32">
            {services.map((service, index) => (
              <div 
                key={service.id} 
                id={service.id}
                className="scroll-mt-24"
                data-testid={`service-${service.id}`}
              >
                <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                  {/* Content Side */}
                  <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''} animate-fade-in-up`} style={{ opacity: 0 }}>
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 animate-bounce-subtle ${
                      service.color === 'purple' ? 'bg-hr-purple/10' : 'bg-hr-teal/10'
                    }`}>
                      <service.icon className={`h-4 w-4 animate-pulse ${
                        service.color === 'purple' ? 'text-hr-purple' : 'text-hr-teal'
                      }`} />
                      <span className={`text-sm font-medium ${
                        service.color === 'purple' ? 'text-hr-purple' : 'text-hr-teal'
                      }`}>Service {index + 1}</span>
                    </div>
                    
                    <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-hr-text-primary mb-4">
                      {service.title}
                    </h2>
                    
                    <p className={`text-xl font-medium mb-6 ${
                      service.color === 'purple' ? 'text-hr-purple' : 'text-hr-teal'
                    }`}>
                      {service.tagline}
                    </p>
                    
                    <p className="text-hr-text-secondary text-lg mb-8 leading-relaxed">
                      {service.description}
                    </p>

                    <ul className="space-y-4 mb-10">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-4 group animate-fade-in-up" style={{ animationDelay: `${featureIndex * 0.1}s`, opacity: 0 }}>
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 shadow-md group-hover:shadow-lg ${
                            service.color === 'purple' 
                              ? 'bg-hr-purple-light group-hover:bg-hr-purple' 
                              : 'bg-hr-teal-light group-hover:bg-hr-teal'
                          }`}>
                            <feature.icon className={`h-5 w-5 transition-colors duration-300 ${
                              service.color === 'purple' 
                                ? 'text-hr-purple group-hover:text-white' 
                                : 'text-hr-teal group-hover:text-white'
                            }`} />
                          </div>
                          <span className="text-hr-text-primary text-lg pt-2.5 group-hover:translate-x-1 transition-transform duration-300">{feature.text}</span>
                        </li>
                      ))}
                    </ul>

                    <Link to="/contact" data-testid={`service-${service.id}-cta`}>
                      <Button 
                        variant="outline" 
                        className={`rounded-full px-8 py-6 text-lg font-medium transition-all duration-500 hover:-translate-y-2 hover:scale-105 group ${
                          service.color === 'purple' 
                            ? 'border-2 border-hr-purple text-hr-purple hover:bg-hr-purple hover:text-white' 
                            : 'border-2 border-hr-teal text-hr-teal hover:bg-hr-teal hover:text-white'
                        }`}
                      >
                        Learn More
                        <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>

                  {/* Image Side with 3D Effects */}
                  <div className={`relative ${index % 2 === 1 ? 'lg:col-start-1' : ''} animate-fade-in-up stagger-2`} style={{ opacity: 0 }}>
                    {/* Animated 3D Frame Effect */}
                    <div className={`absolute inset-4 rounded-3xl transform rotate-3 scale-[1.02] opacity-40 blur-sm animate-pulse-slow ${
                      service.color === 'purple' ? 'bg-hr-purple' : 'bg-hr-teal'
                    }`} />
                    <div className={`absolute inset-4 rounded-3xl transform -rotate-2 scale-[1.01] opacity-25 blur-sm animate-pulse-slow stagger-2 ${
                      service.color === 'purple' ? 'bg-hr-teal' : 'bg-hr-purple'
                    }`} />
                    
                    <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl group hover:shadow-3xl transition-all duration-700">
                      <img 
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                      />
                      {/* Animated overlay gradient */}
                      <div className={`absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-700 ${
                        service.color === 'purple' 
                          ? 'bg-gradient-to-tr from-hr-purple via-transparent to-hr-teal' 
                          : 'bg-gradient-to-tr from-hr-teal via-transparent to-hr-purple'
                      }`} />
                      
                      {/* Shimmer effect on hover */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                      </div>
                    </div>
                    
                    {/* Floating decorative elements */}
                    <div className={`absolute -top-6 -right-6 w-24 h-24 rounded-2xl -z-10 animate-float ${
                      service.color === 'purple' ? 'bg-hr-purple/20' : 'bg-hr-teal/20'
                    }`} />
                    <div className={`absolute -bottom-6 -left-6 w-32 h-32 rounded-2xl -z-10 animate-float-slow ${
                      service.color === 'purple' ? 'bg-hr-teal/20' : 'bg-hr-purple/20'
                    }`} />
                    
                    {/* Ping effect */}
                    <div className={`absolute top-4 right-4 w-4 h-4 rounded-full animate-ping opacity-50 ${
                      service.color === 'purple' ? 'bg-hr-purple' : 'bg-hr-teal'
                    }`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-hr-purple via-hr-purple-dark to-hr-purple relative overflow-hidden" data-testid="services-cta">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-40 h-40 bg-white/5 rounded-full blur-2xl animate-float" />
          <div className="absolute bottom-20 right-20 w-60 h-60 bg-hr-teal/10 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-blob -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute top-1/4 right-1/4 w-20 h-20 bg-hr-teal/20 rounded-full blur-xl animate-bounce-slow" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 mb-6">
            <Zap className="h-4 w-4 text-hr-teal animate-pulse" />
            <span className="text-white/90 text-sm font-medium">Take Action</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-6 animate-fade-in-up">
            Ready to Get Started?
          </h2>
          <p className="text-white/80 text-lg mb-12 max-w-2xl mx-auto animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
            Contact us today to learn more about our services and how we can help you thrive in your career and life.
          </p>
          <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
            <Link to="/contact" data-testid="services-contact-us-btn">
              <Button className="group bg-white text-hr-purple hover:bg-stone-100 rounded-full px-10 py-7 text-lg font-medium shadow-2xl hover:shadow-white/30 transition-all duration-500 hover:-translate-y-3 hover:scale-105">
                Contact Us
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
